import React, { useState } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Clock,
  CheckCircle2,
} from 'lucide-react';

export default function TaskCalendarView({ tasks = [], onTaskClick }) {
  const [currentDate, setCurrentDate] = useState(new Date());

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const handlePrevMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Build 35 or 42 grid cells for current month
  const firstDayIndex = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Previous month trailing days
  for (let i = firstDayIndex - 1; i >= 0; i--) {
    calendarDays.push({
      day: daysInPrevMonth - i,
      month: month - 1,
      year: month === 0 ? year - 1 : year,
      isCurrentMonth: false,
    });
  }

  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({
      day: d,
      month,
      year,
      isCurrentMonth: true,
    });
  }

  // Next month leading days to complete grid
  const remainingDays = 42 - calendarDays.length;
  for (let d = 1; d <= remainingDays; d++) {
    calendarDays.push({
      day: d,
      month: month + 1,
      year: month === 11 ? year + 1 : year,
      isCurrentMonth: false,
    });
  }

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const isToday = (cell) => {
    const today = new Date();
    return (
      cell.day === today.getDate() &&
      cell.month === today.getMonth() &&
      cell.year === today.getFullYear()
    );
  };

  return (
    <div className="task-calendar-wrapper">
      {/* Calendar Header Controls */}
      <div className="task-calendar-controls">
        <div className="task-calendar-controls-left">
          <h2 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            {monthNames[month]} {year}
          </h2>
          <button
            onClick={handleToday}
            className="btn-secondary"
            style={{ padding: '4px 10px', fontSize: '12px' }}
          >
            Today
          </button>
        </div>

        <div className="task-calendar-controls-right">
          <button
            onClick={handlePrevMonth}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
            }}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            onClick={handleNextMonth}
            style={{
              background: 'transparent',
              border: '1px solid var(--border-color)',
              color: 'var(--text-primary)',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
            }}
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Calendar Scroll Container - Smooth Horizontal Scroll on Mobile */}
      <div className="task-calendar-scroll-container">
        <div className="task-calendar-inner-grid">
          {/* Weekday headers */}
          <div className="task-calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday) => (
              <div
                key={weekday}
                className="task-calendar-weekday-cell"
              >
                {weekday}
              </div>
            ))}
          </div>

          {/* Calendar 7x6 Grid */}
          <div className="task-calendar-cells">
            {calendarDays.map((cell, idx) => {
              // Find tasks scheduled on this day
              const cellDate = new Date(cell.year, cell.month, cell.day);
              const cellTasks = tasks.filter((t) => {
                if (!t.end_at && !t.start_at) return false;
                const taskDate = new Date(t.end_at || t.start_at);
                return (
                  taskDate.getFullYear() === cell.year &&
                  taskDate.getMonth() === cell.month &&
                  taskDate.getDate() === cell.day
                );
              });

              const todayMatch = isToday(cell);

              return (
                <div
                  key={idx}
                  className={`task-calendar-day-cell ${todayMatch ? 'is-today' : ''} ${cell.isCurrentMonth ? 'is-current-month' : 'is-other-month'}`}
                >
                  {/* Date number */}
                  <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <span
                      className={`task-calendar-date-badge ${todayMatch ? 'is-today' : ''}`}
                    >
                      {cell.day}
                    </span>
                  </div>

                  {/* Tasks on this day */}
                  <div className="task-calendar-cell-tasks">
                    {cellTasks.map((t) => (
                      <div
                        key={t.id}
                        onClick={() => onTaskClick && onTaskClick(t)}
                        className={`task-calendar-chip priority-${t.priority?.toLowerCase() || 'medium'}`}
                        style={{
                          background:
                            t.status === 'COMPLETED'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : t.priority === 'URGENT'
                              ? 'rgba(239, 68, 68, 0.2)'
                              : 'rgba(59, 130, 246, 0.2)',
                          color:
                            t.status === 'COMPLETED'
                              ? 'var(--success)'
                              : t.priority === 'URGENT'
                              ? 'var(--danger)'
                              : 'var(--accent-primary)',
                          borderLeft: `2px solid ${
                            t.status === 'COMPLETED'
                              ? 'var(--success)'
                              : t.priority === 'URGENT'
                              ? 'var(--danger)'
                              : 'var(--accent-primary)'
                          }`,
                        }}
                        title={`${t.title} (${t.status})`}
                      >
                        {t.title}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
